import React, { useMemo, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import SolidosTotalesRows from "./SolidosTotalesRows";
import { CurrencyDisplay, CurrencyInput, KgDisplay, KgInput } from "../../../Utils/Inputs";
import { MonthHeader } from "../../../Utils/TableComponents";
import { getNextMonth } from "../../../Utils/Utils";

export default function SolidosMonth(props) {
    const { month, monthIndex } = props;

    const { control, setValue } = useFormContext();

    const isFirstTable = month === 'enero';
    const SolidosDataCostoMP = useWatch({
        control,
        name: 'ComparativoCostoMP.SolidosData.SalidasAProduccion',
    });
    const ProduccionFincaData = useWatch({
        control,
        name: 'ComparativoCostoMP.SolidosData.ProduccionFinca',
    });
    const BaseSolidosDataCV = 'ComparativoCostoVentas.SolidosData';

    // Entradas MP a producción
    const entradasMPAProduccionKgSecos = SolidosDataCostoMP[month].KgSecos;
    const entradasMPAProduccionCostoKgSec = SolidosDataCostoMP[month].CostoKgSec;
    const entradasMPAProduccionValorTotal = useMemo(() =>
        entradasMPAProduccionKgSecos * entradasMPAProduccionCostoKgSec,
        [entradasMPAProduccionKgSecos, entradasMPAProduccionCostoKgSec]
    );

    // (+) Gtos de compra de MP
    const gtosDeCompraDeMPCostoKgSec = useWatch({
        control,
        name: `${BaseSolidosDataCV}.GtosDeCompraDeMP.${month}.CostoKgSec`,
    });
    // Calcular: (Salidas a producción - Producción Finca) del Comparativo de Costo de MP
    const salidasMenosProduccionFinca = useMemo(() => {
        const salidasKgSecos = SolidosDataCostoMP[month].KgSecos || 0;
        const produccionFincaKgSecos = ProduccionFincaData && ProduccionFincaData[month] && ProduccionFincaData[month].KgSecos || 0;
        return salidasKgSecos - produccionFincaKgSecos;
    }, [SolidosDataCostoMP, ProduccionFincaData, month]);
    
    const gtosDeCompraDeMPValorTotal = useMemo(() => 
        (gtosDeCompraDeMPCostoKgSec || 0) * salidasMenosProduccionFinca, 
        [gtosDeCompraDeMPCostoKgSec, salidasMenosProduccionFinca]
    );

    // (+) Gtos. Fabricación del mes
    const gtosFabricacionDelMesCostoKgSec = useWatch({
        control,
        name: `${BaseSolidosDataCV}.GtosFabricacionDelMes.${month}.CostoKgSec`,
    });
    const gtosFabricacionDelMesValorTotal = useMemo(() => 
        (gtosFabricacionDelMesCostoKgSec || 0) * entradasMPAProduccionKgSecos, 
        [gtosFabricacionDelMesCostoKgSec, entradasMPAProduccionKgSecos]
    );

    // (=) Producción del mes
    const produccionDelMesKgSecos = useMemo(() =>
        entradasMPAProduccionKgSecos,
        [entradasMPAProduccionKgSecos]
    );
    const produccionDelMesValorTotal = useMemo(() =>
        entradasMPAProduccionValorTotal + gtosDeCompraDeMPValorTotal + gtosFabricacionDelMesValorTotal,
        [entradasMPAProduccionValorTotal, gtosDeCompraDeMPValorTotal, gtosFabricacionDelMesValorTotal]
    );
    const produccionDelMesCostoKgSec = useMemo(() =>
        produccionDelMesKgSecos > 0 ? produccionDelMesValorTotal / produccionDelMesKgSecos : 0,
        [produccionDelMesValorTotal, produccionDelMesKgSecos]
    );

    // (+) Prod. Term. Mes anterior
    const prodTermMesAnteriorKgSecos = useWatch({
        control,
        name: `${BaseSolidosDataCV}.ProdTermMesAnterior.${month}.KgSecos`,
    });
    const prodTermMesAnteriorCostoKgSec = useWatch({
        control,
        name: `${BaseSolidosDataCV}.ProdTermMesAnterior.${month}.CostoKgSec`,
    });
    const prodTermMesAnteriorValorTotal = useMemo(() =>
        prodTermMesAnteriorKgSecos * prodTermMesAnteriorCostoKgSec,
        [prodTermMesAnteriorKgSecos, prodTermMesAnteriorCostoKgSec]
    );

    // (=) Prod. Term. Disponible
    const prodTermDisponibleKgSecos = useMemo(() =>
        produccionDelMesKgSecos + prodTermMesAnteriorKgSecos,
        [produccionDelMesKgSecos, prodTermMesAnteriorKgSecos]
    );
    const prodTermDisponibleValorTotal = useMemo(() =>
        produccionDelMesValorTotal + prodTermMesAnteriorValorTotal,
        [produccionDelMesValorTotal, prodTermMesAnteriorValorTotal]
    );
    const prodTermDisponibleCostoKgSec = useMemo(() =>
        prodTermDisponibleKgSecos > 0 ? prodTermDisponibleValorTotal / prodTermDisponibleKgSecos : 0,
        [prodTermDisponibleValorTotal, prodTermDisponibleKgSecos]
    );

    // (-) Costo Vta. Del Mes
    const costoVtaDelMesKgSecos = useWatch({
        control,
        name: `${BaseSolidosDataCV}.CostoVtaDelMes.${month}.KgSecos`,
    });
    const costoVtaDelMesCostoKgSec = useMemo(() => {
        if (costoVtaDelMesKgSecos > 0) return prodTermDisponibleCostoKgSec;
        return 0;
    }, [costoVtaDelMesKgSecos, prodTermDisponibleCostoKgSec]);
    const costoVtaDelMesValorTotal = useMemo(() =>
        costoVtaDelMesKgSecos * costoVtaDelMesCostoKgSec,
        [costoVtaDelMesKgSecos, costoVtaDelMesCostoKgSec]
    );

    // (=) Prod. Termin. Final
    const prodTerminFinalKgSecos = useMemo(() =>
        prodTermDisponibleKgSecos - costoVtaDelMesKgSecos,
        [prodTermDisponibleKgSecos, costoVtaDelMesKgSecos]
    );
    const prodTerminFinalCostoKgSec = useMemo(() =>
        prodTermDisponibleCostoKgSec,
        [prodTermDisponibleCostoKgSec]
    );
    const prodTerminFinalValorTotal = useMemo(() =>
        prodTerminFinalKgSecos * prodTerminFinalCostoKgSec,
        [prodTerminFinalKgSecos, prodTerminFinalCostoKgSec]
    );

    // CASOS ESPECIALES
    // Prod. Term. Mes anterior para enero y meses posteriores
    let prodTermMesAnteriorRow = null;
    if (!isFirstTable) {
        prodTermMesAnteriorRow = (
            <tr>
                <td><KgDisplay value={prodTermMesAnteriorKgSecos} /></td>
                <td><CurrencyDisplay value={prodTermMesAnteriorCostoKgSec} decimalScale={5} /></td>
                <td><CurrencyDisplay value={prodTermMesAnteriorValorTotal} /></td>
            </tr>
        );
    } else {
        prodTermMesAnteriorRow = (
            <tr>
                <td>
                    <KgInput
                        name={`${BaseSolidosDataCV}.ProdTermMesAnterior.${month}.KgSecos`} />
                </td>
                <td>
                    <CurrencyInput
                        name={`${BaseSolidosDataCV}.ProdTermMesAnterior.${month}.CostoKgSec`}
                        decimalScale={5} />
                </td>
                <td><CurrencyDisplay value={prodTermMesAnteriorValorTotal} /></td>
            </tr>
        );
    }

    // Actualizar valores de Prod. Term. Mes anterior
    useEffect(() => {
        const nextMonth = getNextMonth(monthIndex);
        if (nextMonth === 'enero') return;

        setValue(`${BaseSolidosDataCV}.ProdTermMesAnterior.${nextMonth}.KgSecos`, prodTerminFinalKgSecos);
        setValue(`${BaseSolidosDataCV}.ProdTermMesAnterior.${nextMonth}.CostoKgSec`, prodTerminFinalCostoKgSec);
    }, [prodTerminFinalKgSecos, prodTerminFinalCostoKgSec, month, setValue]);

    // Para guardar Costo Vta Del Mes en CostoKgSec
    useEffect(() => {
        setValue(`${BaseSolidosDataCV}.CostoVtaDelMes.${month}.CostoKgSec`, costoVtaDelMesCostoKgSec);
    }, [costoVtaDelMesCostoKgSec, month, setValue]);

    return (
        <table key={monthIndex}>
            <MonthHeader month={month} monthIndex={monthIndex} />
            <tbody>
                {/* Entradas MP a producción */}
                <tr>
                    <td><KgDisplay value={entradasMPAProduccionKgSecos} /></td>
                    <td><CurrencyDisplay value={entradasMPAProduccionCostoKgSec} decimalScale={5} /></td>
                    <td><CurrencyDisplay value={entradasMPAProduccionValorTotal} /></td>
                </tr>
                {/* (+) Gtos de compra de MP */}
                <tr>
                    <td></td>
                    <td>
                        <CurrencyInput
                            name={`${BaseSolidosDataCV}.GtosDeCompraDeMP.${month}.CostoKgSec`}
                            decimalScale={5} />
                    </td>
                    <td><CurrencyDisplay value={gtosDeCompraDeMPValorTotal} /></td>
                </tr>
                {/* (+) Gtos. Fabricación del mes */}
                <tr>
                    <td></td>
                    <td>
                        <CurrencyInput
                            name={`${BaseSolidosDataCV}.GtosFabricacionDelMes.${month}.CostoKgSec`}
                            decimalScale={5} />
                    </td>
                    <td><CurrencyDisplay value={gtosFabricacionDelMesValorTotal} /></td>
                </tr>
                {/* (=) Producción del mes */}
                <tr className="celda-totales">
                    <td><KgDisplay value={produccionDelMesKgSecos} /></td>
                    <td><CurrencyDisplay value={produccionDelMesCostoKgSec} decimalScale={5} /></td>
                    <td><CurrencyDisplay value={produccionDelMesValorTotal} /></td>
                </tr>
                {/* (+) Prod. Term. Mes anterior */}
                {prodTermMesAnteriorRow}
                {/* (=) Prod. Term. Disponible */}
                <tr className="celda-totales">
                    <td><KgDisplay value={prodTermDisponibleKgSecos} /></td>
                    <td><CurrencyDisplay value={prodTermDisponibleCostoKgSec} decimalScale={5} /></td>
                    <td><CurrencyDisplay value={prodTermDisponibleValorTotal} /></td>
                </tr>
                {/* (-) Costo Vta. Del Mes */}
                <tr>
                    <td>
                        <KgInput
                            name={`${BaseSolidosDataCV}.CostoVtaDelMes.${month}.KgSecos`} />
                    </td>
                    <td><CurrencyDisplay value={costoVtaDelMesCostoKgSec} decimalScale={5} /></td>
                    <td><CurrencyDisplay value={costoVtaDelMesValorTotal} /></td>
                </tr>
                {/* (=) Prod. Termin. Final */}
                <tr className="celda-totales">
                    <td><KgDisplay value={prodTerminFinalKgSecos} /></td>
                    <td><CurrencyDisplay value={prodTerminFinalCostoKgSec} decimalScale={5} /></td>
                    <td><CurrencyDisplay value={prodTerminFinalValorTotal} /></td>
                </tr>
            </tbody>
        </table>
    );
}

